/* global __filename */

/**
 * @typedef {"buy" | "sell"} Side
 *
 * @typedef {import("axios").AxiosInstance} Remote
 *
 * @typedef {string} UUID
 *
 * Verification Payload
 * @typedef {Object} VerificationPayload
 * @property {string} code Verification code provided sent to the users device.
 * @property {"sell"|"buy"} type Buy Side Or Sell Side
 * @property {UUID} orderId
 *
 * @typedef {Object} APIPayload
 * @property {VerificationPayload} payload
 *
 * @typedef {Object} URLOptions
 * @property {UUID} orderUUID
 * @property {UUID} orderId
 * @property {UUID} token
 * @property {Side} type
 * @property {Number} code
 *
 */

import { path, pickBy, complement, isNil } from "ramda";
import { logging as emission } from "../Emission/logging";
import { sendBugsnagError } from "@utils/Bugsnag";
import { SET_OPEN_ORDERS } from "@constants/actionTypes";
const errorResponseMessage = path(["response", "data", "message", 0]);
const stripNull = pickBy(complement(isNil));

// NOTE: An orderId is technically an orderUUID. I think we should be using orderUUID
//       explicitly around the code base since we are context switching between an Int and a UUID.
//       Ideally this prevents confusion.
/**
 * getOrderValidationUrl
 * @param {URLOptions} param0
 * @throws {import('../Emission").Logging}
 */
const getOrderValidationUrl = ({ token, type, orderUUID }) => {
  if (token != null) return `/v3/orders/validation/${token}`;

  if (/(buy|sell)/.test(type) && orderUUID != null)
    return `/v3/orders/${type}/${orderUUID}/validation`;

  throw emission(
    `Failed to get GET order verification url. ${emission.objToKVString({
      token,
      type,
      orderUUID
    })}`
  )
    .method(getOrderValidationUrl)
    .file(__filename)
    .fire();
};

/**
 * getOrderValidationUrl
 * @param {URLOptions} param0
 * @throws {import('../Emission").Logging}
 */
const postOrderValidationUrl = ({ token, type, orderUUID }) => {
  if (token != null) return "/v3/orders/validation";

  if (/(buy|sell)/.test(type) && typeof orderUUID === "string")
    return `/v3/orders/${type}/${orderUUID}/validation`;

  throw emission(
    `Failed to get POST order verification url. ${emission.objToKVString({
      token,
      type,
      orderUUID
    })}`
  )
    .method(postOrderValidationUrl)
    .file(__filename)
    .fire();
};

/**
 * getOrderDetails.
 * @param {Axios} remote a configured axios instance.
 * @param {URLOptions} param1 information required by endpoint to verify an order.
 */
export const getOrderDetails = async (remote, { token, type, orderId }) => {
  try {
    const url = getOrderValidationUrl({ token, type, orderUUID: orderId });

    const response = await remote.get(url);

    if (response.data.verifiableNumbers == null) {
      response.data.verifiableNumbers = [];
    }

    return response.data;
  } catch (error) {
    sendBugsnagError(error, "Modules/OrderVerification/api/getOrderDetails");
    throw emission("Failed to get order information.")
      .method(getOrderDetails)
      .error(error)
      .file(__filename)
      .fire();
  }
};

/**
 * Verify a pending order.
 * @param {Axios} remote a configured axios instance.
 * @param {Payload} param1 information required by endpoint to verify an order.
 */
export const verifyOrder = async (
  remote,
  { payload, type, orderUUID, token }
) => {
  try {
    const response = await remote.post(
      postOrderValidationUrl({ token, type, orderUUID }),
      stripNull(payload)
    );

    return response.data;
  } catch (error) {
    sendBugsnagError(error, "Modules/OrderVerification/api/verifyOrder");
    let msg;
    if (error.response) {
      const data = Object.freeze(error.response.data);
      if (data.message == null) {
        msg = "Failed to verify order.";
      } else if (Array.isArray(data.message)) {
        msg = data.message.join("\n");
      } else {
        msg = data.message;
      }
    } else {
      msg = error.message;
    }

    throw emission(msg).method(verifyOrder).file(__filename).fire();
  }
};

/**
 * @param {Remote} remote
 * @param {URLOptions} param1
 */
export const sendVerificationCode = async (
  remote,
  { code, orderId, type },
  signedIn,
  dispatch
) => {
  try {
    // NOTE: i need to.
    await remote.put(
      `v3/orders/${type}/${orderId}/validation`,
      stripNull({ code })
    );
    if (signedIn) {
      const customerProfile = await remote.get("v3/customers");
      dispatch({
        type: SET_OPEN_ORDERS,
        payload: customerProfile.data.customer.openOrders
      });
    }

    return null;
  } catch (error) {
    sendBugsnagError(
      error,
      "Modules/OrderVerification/api/sendVerificationCode"
    );
    let message = errorResponseMessage(error);
    if (message) return message;

    emission("Non HTTP Failure: " + error.toString())
      .file(__filename)
      .method(sendVerificationCode.name)
      .phoneHome();

    return "Failed to submit verification code";
  }
};
